import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import googleSheets from './services/googleSheets';
import GoogleSignIn from './components/Auth/GoogleSignIn';
import OnboardingWidget from './components/OnboardingWidget';
import TeamDashboard from './components/TeamDashboard';
import AddCustomerForm from './components/AddCustomerForm';
import PriceCalculator from './components/PriceCalculator';
import TransactionalPriceCalculator from './components/TransactionalPriceCalculator';
import ClientPerformanceReport from './components/ClientPerformanceReport';
import ErrorBoundary from './components/ErrorBoundary';
import MotivationalLoader from './components/MotivationalLoader';
import Sidebar from './components/Sidebar';
import { SecureTokenManager } from './utils/secureTokenManager';
import { useUser } from './contexts/UserContext';
import './App.css';

const App: React.FC = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tokenExpiresAt, setTokenExpiresAt] = useState<number | null>(null);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [showExpiryWarning, setShowExpiryWarning] = useState(false);
  const tokenClientRef = useRef<any>(null);
  const { fetchUserInfo, user } = useUser();

  // Get customerId from URL params with memoization (must be before conditional returns)
  const customerId = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('customerId');
  }, []);

  useEffect(() => {
    initializeGoogleAPI();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize token client for silent refresh
  useEffect(() => {
    if (typeof window !== 'undefined' && window.google && isInitialized) {
      const userEmail = SecureTokenManager.getUserEmail();
      tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        scope: 'https://www.googleapis.com/auth/spreadsheets https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile',
        hint: userEmail || undefined,
        callback: async (tokenResponse: any) => {
          if (tokenResponse.access_token) {
            const staySignedIn = SecureTokenManager.getStaySignedIn();
            SecureTokenManager.setToken(
              tokenResponse.access_token,
              tokenResponse.expires_in || 3600,
              userEmail || undefined,
              staySignedIn
            );
            googleSheets.setAccessToken(tokenResponse.access_token);
            setTokenExpiresAt(Date.now() + (tokenResponse.expires_in || 3600) * 1000);
            setShowExpiryWarning(false);
            setError(null);
          }
        },
      });
    }
  }, [isInitialized]);

  // Monitor token expiry and perform automatic renewal
  useEffect(() => {
    if (!isSignedIn || !tokenExpiresAt) return;

    const checkTokenExpiry = () => {
      const now = Date.now();
      const timeLeft = tokenExpiresAt - now;
      
      // Show warning when 5 minutes left
      if (timeLeft <= 5 * 60 * 1000 && timeLeft > 0) {
        setShowExpiryWarning(true);
      }
      
      // Attempt silent refresh when 10 minutes left
      if (timeLeft <= 10 * 60 * 1000 && timeLeft > 0) {
        const staySignedIn = SecureTokenManager.getStaySignedIn();
        const userEmail = SecureTokenManager.getUserEmail();
        
        if (staySignedIn && userEmail && tokenClientRef.current) {
          // Attempt silent token refresh
          tokenClientRef.current.requestAccessToken({ prompt: '' });
        }
      }
      
      // If token expired
      if (timeLeft <= 0) {
        setIsSignedIn(false);
        setError('Your session has expired. Please sign in again.');
      }
    };

    // Check immediately
    checkTokenExpiry();

    // Check every 30 minutes instead of 30 seconds
    const interval = setInterval(checkTokenExpiry, 30 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isSignedIn, tokenExpiresAt]);

  const initializeGoogleAPI = useCallback(async () => {
    try {
      setError(null);
      await googleSheets.initialize();
      setIsInitialized(true);
      
      // Check if already signed in using secure token manager
      const signedIn = googleSheets.isSignedIn();
      setIsSignedIn(signedIn);
      
      // Set access token if available
      if (signedIn) {
        const token = SecureTokenManager.getToken();
        if (token) {
          googleSheets.setAccessToken(token);
          const expiry = SecureTokenManager.getTokenExpiry();
          setTokenExpiresAt(expiry);
          
          // Fetch user info if not already fetched
          if (!user) {
            fetchUserInfo(token);
          }
        } else {
          // Token expired, sign out
          setIsSignedIn(false);
        }
      }
    } catch (error) {
      setError('Failed to initialize Google API. Please check your configuration.');
    } finally {
      setLoading(false);
    }
  }, [fetchUserInfo, user]);

  const handleSignInSuccess = useCallback(async () => {
    setIsSignedIn(true);
    setError(null);
    const expiry = SecureTokenManager.getTokenExpiry();
    setTokenExpiresAt(expiry);
    
    // Fetch user info with the new token
    const token = SecureTokenManager.getToken();
    if (token) {
      await fetchUserInfo(token);
    }
    
    // Create sample data on first sign-in
    await googleSheets.createSampleData().catch(() => {/* Sample data creation failed - continue silently */});
    
    // Initialize transactional pricing data if needed
    await googleSheets.initializeTransactionalPricingData().catch(() => {/* Transactional pricing initialization failed - continue silently */});
  }, [fetchUserInfo]);

  const handleSignOut = useCallback(async () => {
    await googleSheets.signOut();
    setIsSignedIn(false);
    setError(null);
  }, []);

  const handleSidebarStateChange = useCallback((isExpanded: boolean) => {
    setSidebarExpanded(isExpanded);
  }, []);

  if (loading) {
    return (
      <div className="app-loading">
        <MotivationalLoader type="general" size="lg" />
      </div>
    );
  }

  if (!isInitialized && !loading) {
    return (
      <div className="app-error">
        <p>{error || 'Failed to initialize Google API. Please check your configuration.'}</p>
        <button onClick={initializeGoogleAPI} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  if (!isSignedIn) {
    return <GoogleSignIn onSuccess={handleSignInSuccess} />;
  }


  return (
    <ErrorBoundary>
      <Router>
        <div className={`app app-with-sidebar ${sidebarExpanded ? 'app-with-sidebar-expanded' : 'app-with-sidebar-collapsed'}`}>
          <Sidebar 
            onSignOut={handleSignOut} 
            onSidebarStateChange={handleSidebarStateChange}
          />
          
          {showExpiryWarning && (
            <div style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeaa7',
              borderRadius: '4px',
              padding: '12px 16px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              zIndex: 1000,
              maxWidth: '350px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#856404', fontSize: '14px' }}>
                  Your session will expire in less than 5 minutes. 
                  {SecureTokenManager.getStaySignedIn() ? ' It will be automatically renewed.' : ' Please save your work.'}
                </span>
                <button
                  onClick={() => setShowExpiryWarning(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    fontSize: '16px',
                    color: '#856404'
                  }}
                >
                  ×
                </button>
              </div>
            </div>
          )}
          
          <main className="app-main">
            <ErrorBoundary>
              <Routes>
                <Route 
                  key="home"
                  path="/" 
                  element={
                    customerId ? (
                      <ErrorBoundary>
                        <OnboardingWidget customerId={customerId} />
                      </ErrorBoundary>
                    ) : (
                      <Navigate to="/dashboard" />
                    )
                  } 
                />
                <Route 
                  key="dashboard"
                  path="/dashboard" 
                  element={
                    <ErrorBoundary>
                      <TeamDashboard />
                    </ErrorBoundary>
                  } 
                />
                <Route 
                  key="add-customer"
                  path="/add-customer" 
                  element={
                    <ErrorBoundary>
                      <AddCustomerForm />
                    </ErrorBoundary>
                  } 
                />
                <Route 
                  key="price-calculator"
                  path="/price-calculator" 
                  element={
                    <ErrorBoundary>
                      <PriceCalculator />
                    </ErrorBoundary>
                  } 
                />
                <Route 
                  key="transactional-price-calculator"
                  path="/transactional-price-calculator" 
                  element={
                    <ErrorBoundary>
                      <TransactionalPriceCalculator />
                    </ErrorBoundary>
                  } 
                />
                <Route 
                  key="client-performance"
                  path="/client-performance" 
                  element={
                    <ErrorBoundary>
                      <ClientPerformanceReport />
                    </ErrorBoundary>
                  } 
                />
              </Routes>
            </ErrorBoundary>
          </main>
        </div>
      </Router>
    </ErrorBoundary>
  );
};

export default App;